import React, { useRef, useEffect } from 'react';
import { Box, Text, Button, Card, CardHeader, CardContent, TagBadge } from '@chatsdet/ui';
import { Play, Stop, Trash, ArrowDown } from '@phosphor-icons/react';
import type { InstanceLogEntry, InstanceLogsStreamState } from '@chatsdet/types';

type InstanceLogViewerProps = {
  /** Current stream state */
  state: InstanceLogsStreamState;
  /** Whether the stream is connected */
  isConnected: boolean;
  /** Callback to start streaming */
  onConnect: () => void;
  /** Callback to stop streaming */
  onDisconnect: () => void;
  /** Callback to clear logs */
  onClear: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Display variant: 'card' wraps in a Card, 'full' fills the parent container */
  variant?: 'card' | 'full';
};

/**
 * Format timestamp for display
 */
function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Individual log line component
 */
function LogLine({ log }: { log: InstanceLogEntry }) {
  const levelColorClass = {
    stdout: 'text-foreground',
    stderr: 'text-amber-700 dark:text-amber-300',
    info: 'text-blue-700 dark:text-blue-300',
    warn: 'text-amber-700 dark:text-amber-300',
    error: 'text-red-700 dark:text-red-300',
  }[log.level ?? 'stdout'];

  return (
    <div className="flex gap-2 font-mono text-xs py-0.5 hover:bg-background/50 px-2">
      <span className="text-muted-foreground select-none shrink-0 w-20">
        {formatTimestamp(log.timestamp)}
      </span>
      {log.stream && (
        <span className="text-muted-foreground select-none shrink-0 w-24 truncate">
          [{log.stream}]
        </span>
      )}
      <span className={`${levelColorClass} break-all whitespace-pre-wrap`}>{log.message}</span>
    </div>
  );
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: InstanceLogsStreamState['status'] }) {
  switch (status) {
    case 'idle':
      return <TagBadge name="Idle" color="gray" hideDot />;
    case 'connecting':
      return <TagBadge name="Connecting..." color="gray" hideDot />;
    case 'streaming':
      return <TagBadge name="Streaming" color="blue" hideDot />;
    case 'stopped':
      return <TagBadge name="Stopped" color="gray" hideDot />;
    case 'error':
      return <TagBadge name="Error" color="red" hideDot />;
    default:
      return null;
  }
}

/**
 * Container log viewer component with real-time streaming support.
 *
 * Displays Docker container logs from environment instances with
 * auto-scroll, filtering, and streaming controls.
 */
export function InstanceLogViewer({
  state,
  isConnected,
  onConnect,
  onDisconnect,
  onClear,
  className = '',
  variant = 'card',
}: InstanceLogViewerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = React.useState(true);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [state.logs.length, autoScroll]);

  // Detect manual scroll to disable auto-scroll
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  };

  const titleSection = (
    <div className="flex items-center gap-3">
      <Text weight="semibold">Container Logs</Text>
      <StatusBadge status={state.status} />
      {state.logs.length > 0 && (
        <Text size="xs" className="text-muted-foreground">
          ({state.logs.length.toLocaleString()} lines)
        </Text>
      )}
    </div>
  );

  const controls = (
    <div className="flex items-center gap-2">
      {!autoScroll && (
        <Button
          variant="ghost"
          size="sm"
          onPress={() => {
            setAutoScroll(true);
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
          }}
          aria-label="Scroll to bottom"
        >
          <ArrowDown size={16} />
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        onPress={onClear}
        isDisabled={state.logs.length === 0}
        aria-label="Clear logs"
      >
        <Trash size={16} />
      </Button>
      {isConnected ? (
        <Button variant="outline" size="sm" onPress={onDisconnect}>
          <Stop size={14} className="mr-1" />
          Stop
        </Button>
      ) : (
        <Button variant="outline" size="sm" onPress={onConnect}>
          <Play size={14} className="mr-1" />
          Start
        </Button>
      )}
    </div>
  );

  const logScroll = (
    <Box
      ref={scrollRef}
      onScroll={handleScroll}
      className={
        variant === 'full'
          ? 'flex-1 overflow-auto bg-muted py-2'
          : 'overflow-auto bg-muted min-h-[300px] max-h-[500px] py-2'
      }
    >
      {state.logs.length === 0 ? (
        <Text size="sm" className="text-muted-foreground text-center py-8">
          {state.status === 'streaming'
            ? 'Waiting for logs...'
            : 'No logs available. Click Start to begin streaming.'}
        </Text>
      ) : (
        state.logs.map((log, index) => <LogLine key={`${log.timestamp}-${index}`} log={log} />)
      )}
    </Box>
  );

  const errorBanner = state.error ? (
    <Box className="p-2 bg-destructive/10 border-t border-destructive/20">
      <Text size="xs" className="text-destructive">
        {state.error}
      </Text>
    </Box>
  ) : null;

  if (variant === 'full') {
    return (
      <div className={`flex flex-col h-full ${className}`}>
        <div className="flex items-center justify-between px-4 py-2 bg-muted border-b border-border shrink-0">
          {titleSection}
          {controls}
        </div>
        {logScroll}
        {errorBanner}
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        {titleSection}
        {controls}
      </CardHeader>
      <CardContent className="p-0">
        {logScroll}
        {errorBanner}
      </CardContent>
    </Card>
  );
}
